/*
 * Lily & Minx - article presentation
 * LAST UPDATED: 05/10/2026
 * ---------------------------------------------------------------------------
 * Progressive enhancement for the lm-article sections. The article reads
 * normally without it.
 *
 * - Rows: two or more consecutive image-only paragraphs in the article body
 *   become one .lm-media-grid row. Empty editor paragraphs between them do not
 *   break the row. Add the class lm-image--solo to a paragraph or image to keep
 *   it on its own.
 * - Contents: builds the contents list from the body headings (h2, optionally
 *   h3), then the questions and references headings, shows it only when there
 *   are enough entries, and marks the section being read with aria-current
 *   (none above the first heading).
 * - Share: opens the device share sheet, or copies the link and announces it.
 * - Tables (1.3.0): a body table with a header row gets the class
 *   lm-article-table and ARIA table roles, and every body cell its column
 *   heading as data-label. With three or more columns it also gets
 *   lm-article-table--cards and reads as one card per row at every width
 *   (lm-article.css): the reading column is about 490px wide even on a desktop,
 *   too narrow for three columns. The roles keep the table semantics that
 *   display: block removes in some browsers.
 *
 * Idempotent, and re-runs when the theme editor reloads a section.
 * Adapted from the Gro article script. The contents list is checked at most
 * once a frame while the page scrolls (1.1.0; the IntersectionObserver used
 * before missed jumps that no heading crossed).
 */

const SOLO = 'lm-image--solo';
const TABLE = 'lm-article-table';
const CARDS = 'lm-article-table--cards';

const slugify = (value) => value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const isImageOnly = (element) => {
  if (!element.matches('p, figure')) return false;
  if (element.closest('.lm-media')) return false;
  const images = element.querySelectorAll('img');
  if (images.length !== 1) return false;
  if (element.classList.contains(SOLO) || images[0].classList.contains(SOLO)) return false;
  return element.textContent.trim() === '';
};

const isFiller = (element) => !element.querySelector('img, iframe, video') && element.textContent.trim() === '';

export const groupImages = (body) => {
  let run = [];
  let pending = [];
  let absorbed = [];
  const flush = () => {
    if (run.length > 1) {
      absorbed.forEach((filler) => filler.remove());
      const row = document.createElement('div');
      row.className = 'lm-media-grid lm-article__media-run';
      run[0].before(row);
      run.forEach((item) => row.append(item));
    }
    run = [];
    pending = [];
    absorbed = [];
  };
  [...body.children].forEach((element) => {
    if (isImageOnly(element)) {
      if (run.length) absorbed.push(...pending);
      pending = [];
      run.push(element);
    } else if (run.length && isFiller(element)) {
      pending.push(element);
    } else {
      flush();
    }
  });
  flush();
};

const buildContents = (root, body) => {
  root.querySelectorAll('[data-lm-toc]').forEach((toc) => {
    const list = toc.querySelector('.lm-toc__list');
    if (!list) return;
    list.replaceChildren();
    const levels = (toc.dataset.lmTocLevels || 'h2').split(',');
    const minimum = Number(toc.dataset.lmTocMin || 3);
    /* Article headings only: not the headings inside calls to action, signup forms or media. */
    const headings = [...body.querySelectorAll(levels.join(','))].filter((h) => !h.closest('.lm-cta, .lm-article__signup, .lm-media, .lm-notice'));
    /* Then the first heading of each region after the body (questions, further reading), in page order. */
    const extras = [...root.querySelectorAll('[data-lm-toc-extra]')].map((region) => region.querySelector('h2, h3')).filter(Boolean);
    const targets = [...headings, ...extras];
    targets.forEach((heading, index) => {
      if (!heading.id) heading.id = `${slugify(heading.textContent) || 'section'}-${index + 1}`;
      const item = document.createElement('li');
      item.className = `lm-toc__item lm-toc__item--${heading.tagName.toLowerCase()}`;
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.append(link);
      list.append(item);
    });
    toc.hidden = targets.length < minimum;
    if (toc.hidden) return;

    if (root.lmTocScroll) window.removeEventListener('scroll', root.lmTocScroll);
    const links = new Map([...list.querySelectorAll('a')].map((link) => [link.hash.slice(1), link]));
    /* The current section is the last heading above a line 30% down the viewport,
       checked at most once a frame while the page scrolls. An observer on the
       headings missed jumps that no heading crossed (Home, back to top), and left
       the last section marked at the top of the page. */
    const mark = () => {
      const line = window.innerHeight * 0.3;
      let current = null;
      for (const heading of targets) {
        if (heading.getBoundingClientRect().top > line) break;
        current = heading;
      }
      links.forEach((link, id) => {
        if (current && id === current.id) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    let queued = false;
    root.lmTocScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        mark();
      });
    };
    window.addEventListener('scroll', root.lmTocScroll, { passive: true });
    mark();
  });
};

const bindShare = (root) => {
  root.querySelectorAll('[data-lm-share]').forEach((button) => {
    if (button.dataset.lmShareBound) return;
    button.dataset.lmShareBound = 'true';
    const status = button.parentElement?.querySelector('[data-lm-share-status]');
    button.addEventListener('click', async () => {
      const data = { title: document.title, url: window.location.href };
      if (navigator.share) {
        try {
          await navigator.share(data);
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('lm-article: share failed', error);
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(data.url);
        if (status) status.textContent = button.dataset.lmShareCopied || '';
      } catch (error) {
        console.warn('lm-article: copy failed', error);
      }
    });
  });
};

export const labelTables = (body) => {
  body.querySelectorAll('table').forEach((table) => {
    if (table.classList.contains(TABLE)) return;
    const headers = [...(table.tHead?.rows[0]?.cells ?? [])].map((cell) => cell.textContent.trim());
    if (!headers.length) return;
    table.classList.add(TABLE);
    if (headers.length >= 3) table.classList.add(CARDS);
    table.setAttribute('role', 'table');
    table.querySelectorAll('thead, tbody, tfoot').forEach((group) => group.setAttribute('role', 'rowgroup'));
    [...table.rows].forEach((row) => {
      row.setAttribute('role', 'row');
      const inHead = row.parentElement === table.tHead;
      [...row.cells].forEach((cell, index) => {
        if (inHead) {
          cell.setAttribute('role', 'columnheader');
          return;
        }
        cell.setAttribute('role', cell.tagName === 'TH' ? 'rowheader' : 'cell');
        if (headers[index]) cell.dataset.label = headers[index];
      });
    });
  });
};

export const initialise = (scope = document) => {
  scope.querySelectorAll('[data-lm-article-root]').forEach((root) => {
    const body = root.querySelector('[data-lm-article-body]');
    if (body && !body.dataset.lmArticleReady) {
      body.dataset.lmArticleReady = 'true';
      groupImages(body);
      labelTables(body);
    }
    if (body) buildContents(root, body);
    bindShare(root);
  });
};

if (!window.lmArticleLoaded) {
  window.lmArticleLoaded = true;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
}
